import { useCallback } from "react";

import { Badge2 } from "@/ui2/atoms/Badge";
import type { BaseItem, CosignatureStatus } from "@/core/types";
import { Select2 } from "@/ui2/molecules/Select";

import { ALL_OPTION_ID, STATUS_VARIANT } from "../../constants";
import type { CosignatureTab } from "../../types";

type StatusOptionValue = CosignatureStatus | "all";

type StatusSelectProps = {
  onChange: (value: StatusOptionValue) => void;
  tab: CosignatureTab;
  value: CosignatureStatus | null;
};

export function StatusSelect({ onChange, tab, value }: StatusSelectProps) {
  const handleChange = useCallback(
    (key: Nullable<StatusOptionValue>) => {
      onChange(key === ALL_OPTION_ID ? null : key);
    },
    [onChange],
  );

  const options = StatusOptionsMap[tab];

  const renderItem = useCallback((item: StatusOption) => {
    const badgeVariant = STATUS_VARIANT[item.id as CosignatureStatus];

    if (!badgeVariant) return item.name;

    return (
      <Badge2 appearance={badgeVariant.appearance} variant={badgeVariant.variant}>
        {item.name}
      </Badge2>
    );
  }, []);

  return (
    <Select2
      className="tw:max-w-40 tw:shrink-0"
      label="Status"
      onChange={handleChange}
      options={options}
      renderItem={renderItem}
      value={value ?? ALL_OPTION_ID}
    />
  );
}

const STATUS_OPTIONS = [
  { id: ALL_OPTION_ID, name: "All" },
  { id: "pending", name: "Pending" },
  { id: "resubmitted", name: "Resubmitted" },
  { id: "signed", name: "Signed" },
  { id: "rejected", name: "Rejected" },
] as const satisfies BaseItem<StatusOptionValue>[];

type StatusOption = (typeof STATUS_OPTIONS)[number];

const NEEDS_REVIEW_OPTIONS = [
  { id: ALL_OPTION_ID, name: "All" },
  { id: "pending", name: "Pending" },
  { id: "resubmitted", name: "Resubmitted" },
] as const satisfies BaseItem<StatusOptionValue>[];

const StatusOptionsMap = {
  "all-notes": STATUS_OPTIONS,
  "needs-review": NEEDS_REVIEW_OPTIONS,
} as const satisfies Record<CosignatureTab, BaseItem<StatusOptionValue>[]>;
